<template>
  <div class="npc-editors" :data-testid="`${npcLabel(npc)}-dialogue-editor-container`">
    <div class="old-globals two-column-grid">
      <label for="voice">Voice</label>
      <voice-selector
        :voice-key="npc ? npc.voiceKey : null"
        :voices="voices"
        :readonly="readonly"
        aria-label="NPC Voice Selector"
        data-testid="voice-selector"
        :disabled-voice-keys="disabledForNpc().map(info => info.voiceKey)"
        @update="updateNpcVoice"
      />
      <label v-if="activeTab === DialogueType.AI_NPC_PROMPT || activeTab === DialogueType.AI_NPC_DIALOGUE" for="npc-role-input">
        Role
      </label>
      <div v-if="activeTab === DialogueType.AI_NPC_PROMPT || activeTab === DialogueType.AI_NPC_DIALOGUE">
        <md-field :class="{'md-invalid': false}" :md-counter="true">
          <md-input
            id="npc-role-input"
            aria-label="NPC Role"
            data-testid="role-input"
            :value="npc.role"
            :class="{'error': roleErrors.length}"
            :maxlength="30"
            :disabled="readonly"
            @input="updateRole"
          />
          <help-tooltip v-if="roleErrors && roleErrors.length" type="error">
            <span v-for="error in roleErrors" :key="error.message">
              {{ error.message }}
              <br>
            </span>
          </help-tooltip>
        </md-field>
      </div>
    </div>
    <div
      v-if="activeTab === DialogueType.AI_NPC_PROMPT"
      class="npc-prompt"
      data-testid="prompt-container"
    >
      <html-editor
        label="NPC Prompt"
        subtitle="NPC prompt should include a description of their role, details on their current circumstances, and any goals, objectives, or hinderances."
        placeholder="Write or paste your NPC prompt here"
        data-testid="prompt-editor"
        :contents="promptMarkup"
        :readonly="readonly"
        :reload="state.reloadPrompt"
        :errors="errors.filter(error => (
          error.metadata.componentId === DialogueType.AI_NPC_PROMPT &&
          error.metadata.characterKey === npc.key
        ))"
        :prevent-emojis="true"
        @update="debouncedEditPrompt"
      />
      <prompt-footer
        :case-conf="caseConf"
        prompt-type="npc"
        :readonly="readonly"
        @updateUseCurrentDate="updateUseCurrentDate"
        @replacePrompt="replacePrompt"
      />
    </div>
    <dialogue-editor
      v-if="activeTab === DialogueType.NPC_Q_AND_A"
      :key="'NPC_Q_AND_A' + npc.key"
      class="new-globals"
      data-testid="questions-and-answers-container"
      :settings="npcQuestionSettings"
      :items="npcDialogue.questions || []"
      :readonly="readonly"
      :errors="errors"
      :warnings="warnings"
      :title-prefix="npcLabel(npc) + ': '"
      @update="updateQuestions"
      @playAudio="playAnswerText"
    />
    <dialogue-editor
      v-if="activeTab === DialogueType.NPC_COMMANDS"
      :key="'NPC_COMMANDS' + npc.key"
      class="new-globals"
      data-testid="commands-container"
      :settings="npcCommandSettings"
      :items="npcDialogue.commands || []"
      :readonly="readonly"
      :errors="errors"
      :warnings="warnings"
      :title-prefix="npcLabel(npc) + ': '"
      @update="updateCommands"
      @playAudio="playAnswerText"
    />
    <dialogue-editor
      v-if="activeTab === DialogueType.NPC_UNSOLICITED_DIALOGUE"
      :key="'NPC_UNSOLICITED_DIALOGUE' + npc.key"
      class="new-globals"
      data-testid="unsolicited-container"
      :settings="npcUnsolicitedSettings"
      :items="npcDialogue.unsolicited || []"
      :readonly="readonly"
      :errors="errors"
      :warnings="warnings"
      :title-prefix="npcLabel(npc) + ': '"
      @update="updateUnsolicited"
      @playAudio="playAnswerText"
    />

    <div
      v-if="activeTab === DialogueType.AI_NPC_DIALOGUE"
      class="ai-directed-dialogue"
      data-testid="ai-listen-for-say-this-container"
    >
      <dialogue-editor
        :key="'NPC_AI_QUESTIONS' + npc.key"
        class="ai-questions new-globals"
        data-testid="ai-listen-for-container"
        :settings="npcAiQuestionSettings"
        :items="npcDialogue.aiQuestions || []"
        :readonly="readonly"
        :errors="errors"
        :warnings="warnings"
        @update="updateAiQuestions"
        @playAudio="playAnswerText"
      />
      <dialogue-editor
        :key="'NPC_AI_UNSOLICITED_' + npc.key"
        class="ai-unsolicited new-globals"
        data-testid="ai-say-this-container"
        :settings="npcAiUnsolicitedSettings"
        :items="npcDialogue.aiUnsolicited || []"
        :readonly="readonly"
        :errors="errors"
        :warnings="warnings"
        @update="updateAiUnsolicited"
        @playAudio="playAnswerText"
      />
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import HelpTooltip from '~lib/common/components/HelpTooltip.vue';
import HtmlEditor from '~lib/common/components/HtmlEditor/HtmlEditor.vue';
import { npcForScene } from '~lib/common/utils/npc';
import { npcLabel } from '~lib/common/utils/labels';

import VoiceSelector from '../common/VoiceSelector.vue';
import DialogueEditor from './DialogueEditor.vue';
import PromptFooter from './PromptFooter.vue';
import DialogueType from './DialogueType';

export default {
  name: 'npc-editors',
  components: {
    VoiceSelector,
    HelpTooltip,
    HtmlEditor,
    DialogueEditor,
    PromptFooter,
  },
  props: {
    activeId: {
      type: String,
      required: true,
    },
    activeTab: {
      type: String,
      required: true,
    },
    caseConf: {
      type: Object,
      required: true,
    },
    voices: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    warnings: {
      type: Array,
      default: () => [],
    },
    usedInfo: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      npcQuestionSettings: {
        type: DialogueType.NPC_Q_AND_A,
        onText: 'On-at-Start Questions',
        offText: 'Off-at-Start Questions',
        questionLabel: 'Question',
        answerLabel: 'Answer',
        newQuestionLabel: 'Add a question',
        newAnswerLabel: 'Add an answer',
      },
      npcCommandSettings: {
        type: DialogueType.NPC_COMMANDS,
        onText: 'On-at-Start Commands',
        offText: 'Off-at-Start Commands',
        onSubtitle: 'Commands need to be connected to their desired effects in Case Logic',
        questionLabel: 'Command',
        newQuestionLabel: 'Add a command',
        isAnswerDisabled: true,
      },
      npcUnsolicitedSettings: {
        type: DialogueType.NPC_UNSOLICITED_DIALOGUE,
        offText: 'Unsolicited',
        offSubtitle: 'Unsolicited needs to be triggered by an action or timer in Case Logic',
        onDisabled: true,
        isQuestionDisabled: true,
        answerLabel: 'NPC Unsolicited',
        newAnswerLabel: 'Add a question or statement to say'
      },
      npcAiQuestionSettings: {
        type: DialogueType.NPC_Q_AND_A,
        onText: 'AI: Listen For & Say This',
        onSubtitle: 'Have the AI listen for a specific question and respond with a specific answer',
        offText: 'AI: Say This',
        questionLabel: 'Listen For',
        answerLabel: 'Say This',
        reasonLabel: 'Reason for Response',
        newQuestionLabel: 'Add a question or statement to listen for',
        newAnswerLabel: 'Add a question or answer to be said',
        newReasonLabel: 'Add a reason for this response',
        offDisabled: true,
        isAI: true,
        fullWidth: true,
      },
      npcAiUnsolicitedSettings: {
        type: DialogueType.NPC_UNSOLICITED_DIALOGUE,
        offText: 'AI: Say This',
        offSubtitle: 'Have the AI make specific statements through scenario logic',
        onDisabled: true,
        isQuestionDisabled: true,
        answerLabel: 'Say This',
        reasonLabel: 'Reason for Response',
        newAnswerLabel: 'Add a question or statement to say',
        newReasonLabel: 'Add a reason for this response',
        isAI: true,
        fullWidth: true,
      },
      state: {
        reloadPrompt: false,
      },
      DialogueType,
      npcLabel,
    };
  },
  computed: {
    npc() {
      return npcForScene(this.caseConf.npcs, this.activeId);
    },
    npcDialogue() {
      return get(this.npc, 'dialogue', {}) || {};
    },
    promptMarkup() {
      return get(this.npc, 'dialogue.prompt.markup.en');
    },
    promptContents() {
      return get(this.npc, 'dialogue.prompt.content.en');
    },
    roleErrors() {
      const npc = this.npc;
      return this.errors.filter(error => (
        error.metadata.componentId === 'role' &&
        error.metadata.characterKey === npc.key
      ))
    }
  },
  watch: {
    activeId(newValue, oldValue) {
      if (newValue !== oldValue && this.activeTab === DialogueType.AI_NPC_PROMPT) {
        // Force a load of the prompt when the NPC prompt is being displayed, and the NPC being displayed has changed
        this.replacePrompt({
          markup: this.promptMarkup,
          content: this.promptContents
        });
      }
    }
  },
  methods: {
    disabledForNpc() {
      return this.usedInfo.filter(info => info.key !== this.activeId);
    },
    updateNpcVoice(voiceKey) {
      if (this.npc) {
        this.npc.voiceKey = voiceKey;
        this.$emit('update', {
          npcs: this.caseConf.npcs,
        });
      }
    },
    updateRole(value) {
      this.npc.role = value && value.toUpperCase();
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    editPrompt(value) {
      if (!this.npc.dialogue) {
        this.npc.dialogue = {};
      }
      this.npc.dialogue.prompt = {
        markup: {
          en: value.markup
        },
        content: {
          en: value.content
        }
      };
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    debouncedEditPrompt: debounce(function(value) {
      this.editPrompt(value);
    }, 500),
    updateUseCurrentDate(value) {
      this.$emit('update', {
        dialogue: {
          useCurrentDate: value
        }
      });
    },
    updateQuestions(items) {
      if (!this.npc.dialogue) {
        this.npc.dialogue = {};
      }
      this.npc.dialogue.questions = items;
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    updateCommands(items) {
      if (!this.npc.dialogue) {
        this.npc.dialogue = {};
      }
      this.npc.dialogue.commands = items;
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    updateUnsolicited(items) {
      if (!this.npc.dialogue) {
        this.npc.dialogue = {};
      }
      this.npc.dialogue.unsolicited = items;
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    updateAiQuestions(items) {
      if (!this.npc.dialogue) {
        this.npc.dialogue = {};
      }
      this.npc.dialogue.aiQuestions = items;
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    updateAiUnsolicited(items) {
      if (!this.npc.dialogue) {
        this.npc.dialogue = {};
      }
      this.npc.dialogue.aiUnsolicited = items;
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    playAnswerText(question, withVoiceId) {
      this.$emit('playAnswerText', question, withVoiceId);
    },
    replacePrompt(value) {
      this.editPrompt(value);
      // A hacky way to get the HtmlEditor to reload its contents
      this.state.reloadPrompt = true;
      this.$nextTick(() => {
        this.state.reloadPrompt = false;
      });
    },
  }
}
</script>

<style scoped>
.npc-editors {
  height: 100%;
  padding-top: 12px;
}

.two-column-grid {
  width: 480px;
  margin-bottom: 1rem;
}

.npc-prompt {
  display: flex;
  flex-direction: column;
  height: calc(100% - 100px);
}

.html-editor {
  flex: 1;
}

.ai-directed-dialogue {
  display: flex;
  height: calc(100% - 40px);
}

.ai-unsolicited {
  margin-left: 15px;
}

.ai-directed-dialogue .list-container {
  flex: 1;
}
</style>